// API HTTP client. Wraps fetch with Firebase auth header + JSON serialization
// and triggers an unauthorized callback on 401 so the app can sign the user
// out without this module needing to know about logout/appState directly.

let getAuthToken = () => null;
let refreshAuthToken = null;  // optional async: forces Firebase to mint a fresh ID token
let refreshCurrentUser = null; // optional async: refetches /me to sync appState.currentUserNeo4jId
let onUnauthorized = () => {};

/**
 * Wire the module to the host app. All options are functions so values are
 * read lazily, avoiding any need to re-configure on token refresh.
 *   - getAuthToken:     () => string | null            (called every request)
 *   - refreshAuthToken: async () => string | null      (called once on 401 retry)
 *   - refreshCurrentUser: async () => void             (called once on 403 mismatch retry)
 *   - onUnauthorized:   async () => void               (hard logout after retry fails)
 */
export function configureApi(opts = {}) {
    if (typeof opts.getAuthToken === 'function') getAuthToken = opts.getAuthToken;
    if (typeof opts.refreshAuthToken === 'function') refreshAuthToken = opts.refreshAuthToken;
    if (typeof opts.refreshCurrentUser === 'function') refreshCurrentUser = opts.refreshCurrentUser;
    if (typeof opts.onUnauthorized === 'function') onUnauthorized = opts.onUnauthorized;
}

// Rebuild the URL after a /me refresh may have updated the current user's
// id — a stale currentUserNeo4jId in URLs like /users/<id>/albums is the
// exact class of bug the 403 retry path exists to fix. Regex + replacer
// so we don't parse the URL as a full URL object (relative paths etc.).
// Returns the possibly-updated URL. If getFreshId returns null/undefined
// OR matches the current URL's id, the URL is returned unchanged.
function _rewriteUserIdInUrl(url, freshUserId) {
    if (freshUserId === null || freshUserId === undefined) return url;
    return url.replace(/\/users\/(\d+)(\/|$|\?)/, (match, currentId, sep) => {
        return String(currentId) === String(freshUserId) ? match : `/users/${freshUserId}${sep}`;
    });
}

async function _doFetch(url, options, tokenOverride) {
    let body = options.body;
    const headers = { ...options.headers };
    const token = tokenOverride !== undefined ? tokenOverride : getAuthToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (!(body instanceof FormData)) {
        headers['Content-Type'] = 'application/json';
        body = JSON.stringify(body);
    }
    return fetch(url, { ...options, headers, body });
}

/**
 * fetch wrapper. Adds Authorization: Bearer <token>, sets Content-Type for
 * non-FormData bodies, and handles two idle-tab failure modes with a single
 * retry each before giving up:
 *
 * 1. 401 → force-refresh Firebase token (getIdToken(true)) + retry once.
 *    Handles the case where appState.authToken is stale after a long-idle
 *    tab and Firebase's background refresh missed the window.
 * 2. 403 "Unauthorized access" from a /users/<id>/... path → refetch /me to
 *    resync appState.currentUserNeo4jId + retry with the corrected URL.
 *    Handles the case where currentUserNeo4jId got out of sync with the
 *    signed-in token (BFCache, multi-tab). Prod-observed 2026-07-23:
 *    Photos view after idle produced "Could not load albums: Unauthorized
 *    access" that only a full page refresh cleared.
 *
 * Retries are bounded to ONE per class; if the second attempt still fails,
 * onUnauthorized fires (for 401) or the error propagates (for 403).
 */
export async function apiFetch(url, options = {}) {
    let response = await _doFetch(url, options);

    // 401 self-heal: fresh token retry.
    if (response.status === 401 && refreshAuthToken) {
        try {
            const fresh = await refreshAuthToken();
            if (fresh) {
                response = await _doFetch(url, options, fresh);
            }
        } catch (e) {
            // Falls through to the 401 handler below.
        }
    }

    // 403 mismatch self-heal: refetch /me + retry with corrected URL. Only
    // applied when the error body reads "Unauthorized access" AND the URL
    // targets a user-id path — anywhere else, a 403 is a legitimate
    // ownership rejection that self-heal can't fix.
    if (response.status === 403 && refreshCurrentUser && /\/users\/\d+\//.test(url)) {
        const peek = await response.clone().json().catch(() => null);
        if (peek && /unauthorized access/i.test(peek.error || '')) {
            try {
                const freshId = await refreshCurrentUser();
                const newUrl = _rewriteUserIdInUrl(url, freshId);
                if (newUrl !== url) {
                    response = await _doFetch(newUrl, options);
                }
            } catch (e) {
                // Falls through to the error handler below.
            }
        }
    }

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({
            error: `API request failed with status ${response.status}`
        }));

        if (response.status === 401) {
            console.error('Authentication failed - signing out');
            try { await onUnauthorized(); } catch (e) { /* ignore */ }
        }

        // 2026-10-11: preserve response.status + any structured fields from
        // the backend error payload (reason, data, etc.) alongside .message.
        // Backend endpoints like /invites/accept + /onboarding/complete return
        // JSON like {"error": "...", "reason": "daily_cap_reached"} on 429
        // and callers need to distinguish specific failure classes without
        // string-matching the message. Backward-compatible: existing callers
        // reading error.message continue to work; new callers can inspect
        // error.status / error.reason / error.data.
        const err = new Error(errorData.error);
        err.status = response.status;
        err.reason = errorData.reason || null;
        err.data = errorData;
        throw err;
    }

    if (response.status === 204) return null;
    return response.json();
}
